/**
 * Microsoft Clarity — session replay and heatmaps.
 *
 * One project id covers both surfaces (koaform.com and app.koaform.com), which
 * keeps funnels joined across the marketing → builder handoff. Note Koalendar
 * splits these into two projects (9eewzz5551 site, 9dbr1yh5xp app), so its
 * numbers do not join.
 *
 * The public form renderer (/r/{slug}) deliberately does NOT load Clarity —
 * see the note on masking below.
 */
export const CLARITY_PROJECT_ID = "xz3vmmozqs";
/**
 * MASKING
 *
 * Clarity's global mode lives in the dashboard (Settings → Setup → Masking:
 * Mask all / Mask sensitive / Mask none). There is no JS API for it, so code
 * cannot set it — but `data-clarity-unmask` on a container beats the default,
 * which is how we get relaxed behaviour regardless of what the dashboard says.
 *
 * `applyRelaxedMasking()` stamps that attribute on <body>. Combined with the
 * dashboard set to "Mask none", replays show real text rather than blocks.
 *
 * The reason to still have MASK_ATTR: koaform is a form builder, so some of
 * what renders inside app.koaform.com is not the operator's own content — the
 * responses table and single-response view are full of things strangers typed,
 * which can be resumes, addresses or medical intake answers. Wrap those in
 * `data-clarity-mask="true"` so relaxed masking everywhere else does not
 * quietly ship third-party personal data into replays.
 */
export const UNMASK_ATTR = "data-clarity-unmask";
export const MASK_ATTR = "data-clarity-mask";
/** Spread onto a container holding respondent-submitted data. */
export const maskedRegionProps = { [MASK_ATTR]: "true" };
/** The upstream snippet, verbatim, as a string for inline <script> injection. */
export const clarityInlineSnippet = (projectId = CLARITY_PROJECT_ID) => `(function(c,l,a,r,i,t,y){` +
    `c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};` +
    `t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;` +
    `y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);` +
    `})(window, document, "clarity", "script", "${projectId}");`;
/** Relaxed masking: show real text. Safe here — this is the operator's own UI. */
export const applyRelaxedMasking = (doc = document) => {
    doc.body?.setAttribute(UNMASK_ATTR, "true");
};
/**
 * Loads Clarity into a single-page app.
 *
 * When disabled, installs a no-op `window.clarity` so call sites never have to
 * guard — the same trick Koalendar uses in client/src/services/clarity.ts.
 */
export const initClarity = ({ projectId = CLARITY_PROJECT_ID, enabled = true, } = {}) => {
    if (typeof window === "undefined")
        return;
    if (!enabled) {
        window.clarity ??= (() => { });
        return;
    }
    if (window.clarity)
        return; // already loaded — never inject twice
    const queue = function (...args) {
        (queue.q = queue.q || []).push(args);
    };
    window.clarity = queue;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${projectId}`;
    const first = document.getElementsByTagName("script")[0];
    first?.parentNode?.insertBefore(script, first);
    applyRelaxedMasking();
};
/** Ties a replay to a signed-in user. Never pass anything a respondent typed. */
export const clarityIdentify = (userId) => {
    window.clarity?.("identify", userId);
};
export const claritySetTag = (key, value) => {
    window.clarity?.("set", key, value);
};
//# sourceMappingURL=clarity.js.map